// src/components/MeuPerfilModal.jsx
// Modal "Meu Perfil" com 3 abas: Dados / Senha / Sessões Ativas.
// Usa o token armazenado em localStorage (sem dependência de outro provider).
import React, { useState, useEffect, useCallback } from "react";
import authService from "../services/authService";
import Banner from "./ui/Banner";
import ConfirmModal from "./ui/ConfirmModal";
import {
  pushSuportado, permissaoAtual, statusAssinaturaAtual, servidorHabilitado,
  ativarPush, desativarPush, testarPush,
} from "../utils/pushNotifications";

const ABAS = [
  { id: "dados",    label: "Dados",          icon: "bi-person" },
  { id: "senha",    label: "Senha",          icon: "bi-key" },
  { id: "sessoes",  label: "Sessões",        icon: "bi-shield-lock" },
  { id: "notif",    label: "Notificações",   icon: "bi-bell" },
];

function formatarData(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString("pt-BR"); } catch { return String(dt); }
}

function tempoRelativo(dt) {
  if (!dt) return "—";
  const ms = Date.now() - new Date(dt).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d}d`;
}

export default function MeuPerfilModal({ aberto, abaInicial = "dados", usuario, onFechar, onSenhaTrocada }) {
  const [aba, setAba] = useState(abaInicial);

  useEffect(() => { if (aberto) setAba(abaInicial || "dados"); }, [aberto, abaInicial]);

  if (!aberto) return null;

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(15,23,42,.45)", zIndex:3000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
    }}
    onClick={onFechar}
    >
      <div onClick={e=>e.stopPropagation()}
        style={{
          background:"var(--bg-surface)", borderRadius:14, width:"100%", maxWidth:640,
          maxHeight:"92vh", display:"flex", flexDirection:"column",
          boxShadow:"0 20px 60px rgba(0,0,0,.35)", overflow:"hidden",
        }}>
        {/* Header */}
        <div style={{
          padding:"16px 20px", borderBottom:"1px solid #E5E7EB",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          background:"linear-gradient(135deg,#1B3A6B,#2A5298)", color:"#fff",
        }}>
          <div style={{display:"flex", alignItems:"center", gap:10}}>
            <i className="bi bi-person-circle" style={{fontSize:22}}/>
            <div>
              <div style={{fontWeight:700, fontSize:16}}>Meu Perfil</div>
              <div style={{fontSize:12, opacity:.85}}>{usuario?.email || ""}</div>
            </div>
          </div>
          <button onClick={onFechar}
            style={{background:"transparent",border:"none",color:"#fff",fontSize:18,cursor:"pointer"}}
            aria-label="Fechar">
            <i className="bi bi-x-lg"/>
          </button>
        </div>

        {/* Tabs */}
        <div style={{display:"flex", borderBottom:"1px solid #E5E7EB", background:"#F9FAFB"}}>
          {ABAS.map(a => (
            <button key={a.id} onClick={()=>setAba(a.id)}
              style={{
                flex:1, padding:"12px 8px", border:"none", cursor:"pointer",
                background: aba===a.id ? "#fff" : "transparent",
                color: aba===a.id ? "#1B3A6B" : "#6B7280",
                fontWeight: aba===a.id ? 700 : 500,
                fontSize:13.5,
                borderBottom: aba===a.id ? "2px solid #1B3A6B" : "2px solid transparent",
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              }}>
              <i className={`bi ${a.icon}`}/> {a.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{flex:1, overflowY:"auto", padding:20}}>
          {aba === "dados"   && <AbaDados   usuario={usuario}/>}
          {aba === "senha"   && <AbaSenha   onSucesso={onSenhaTrocada}/>}
          {aba === "sessoes" && <AbaSessoes/>}
          {aba === "notif"   && <AbaNotificacoes/>}
        </div>
      </div>
    </div>
  );
}

// ── Aba: Dados ─────────────────────────────────────────────────────────────
function AbaDados({ usuario }) {
  const linhas = [
    { label:"Nome",    valor: usuario?.nome    || "—" },
    { label:"E-mail",  valor: usuario?.email   || "—" },
    { label:"Perfil",  valor: usuario?.perfil  || "—" },
    { label:"Telefone",valor: usuario?.telefone|| "—" },
  ];
  return (
    <div>
      <div style={{fontSize:13, color:"#6B7280", marginBottom:14}}>
        <i className="bi bi-info-circle me-2"/>
        Para alterar dados cadastrais, contate o administrador da sua conta.
      </div>
      {linhas.map(l => (
        <div key={l.label} style={{
          display:"flex", justifyContent:"space-between", padding:"10px 0",
          borderBottom:"1px solid #F3F4F6", fontSize:14,
        }}>
          <span style={{color:"#6B7280"}}>{l.label}</span>
          <span style={{color:"#111827", fontWeight:500}}>{l.valor}</span>
        </div>
      ))}
    </div>
  );
}

// ── Aba: Senha ─────────────────────────────────────────────────────────────
function AbaSenha({ onSucesso }) {
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha,  setNovaSenha]  = useState("");
  const [confirma,   setConfirma]   = useState("");
  const [erro,       setErro]       = useState("");
  const [msg,        setMsg]        = useState("");
  const [salvando,   setSalvando]   = useState(false);

  const salvar = async (e) => {
    e.preventDefault();
    setErro(""); setMsg("");
    if (!senhaAtual) return setErro("Informe a senha atual.");
    if (novaSenha.length < 6) return setErro("A nova senha deve ter ao menos 6 caracteres.");
    if (novaSenha === senhaAtual) return setErro("A nova senha deve ser diferente da atual.");
    if (novaSenha !== confirma) return setErro("As senhas não conferem.");
    setSalvando(true);
    try {
      await authService.trocarSenha({ senhaAtual, senha: novaSenha, confirmaSenha: confirma });
      setMsg("Senha alterada. Você será desconectado.");
      setSenhaAtual(""); setNovaSenha(""); setConfirma("");
      setTimeout(() => onSucesso?.(), 1500);
    } catch (e) {
      setErro(e?.message || "Erro ao trocar a senha.");
    }
    setSalvando(false);
  };

  return (
    <form onSubmit={salvar}>
      {erro && <Banner tipo="error">{erro}</Banner>}
      {msg  && <Banner tipo="success">{msg}</Banner>}
      <div style={{background:"var(--bg-highlight)", border:"1px solid #BFDBFE", color:"#1D4ED8",
        borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12.5}}>
        <i className="bi bi-shield-lock me-2"/>
        Ao trocar a senha, <strong>todas as suas sessões</strong> em outros dispositivos serão encerradas.
      </div>

      <Campo label="Senha atual" tipo="password" valor={senhaAtual} onChange={setSenhaAtual} autoFocus/>
      <Campo label="Nova senha (mín. 6)"   tipo="password" valor={novaSenha}  onChange={setNovaSenha}/>
      <Campo label="Confirmar nova senha"  tipo="password" valor={confirma}   onChange={setConfirma}/>

      <button type="submit" disabled={salvando}
        style={{
          width:"100%", marginTop:8, padding:"12px", borderRadius:8, border:"none",
          background:"linear-gradient(135deg,#1B3A6B,#2A5298)", color:"#fff",
          fontWeight:700, fontSize:14, cursor:"pointer", opacity: salvando?.7:1,
        }}>
        {salvando ? <><span className="spinner-border spinner-border-sm me-2"/>Salvando…</> : "Alterar senha"}
      </button>
    </form>
  );
}

function Campo({ label, tipo="text", valor, onChange, autoFocus }) {
  return (
    <div style={{marginBottom:12}}>
      <label style={{fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6}}>
        {label}
      </label>
      <input type={tipo} value={valor} onChange={e=>onChange(e.target.value)}
        autoFocus={autoFocus}
        style={{width:"100%", padding:"10px 12px", borderRadius:8,
          border:"1px solid #D1D5DB", fontSize:14, outline:"none", boxSizing:"border-box"}}/>
    </div>
  );
}

// ── Aba: Sessões ───────────────────────────────────────────────────────────
function AbaSessoes() {
  const [sessoes,   setSessoes]   = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro,      setErro]      = useState("");
  const [msg,       setMsg]       = useState("");
  const [confirma,  setConfirma]  = useState(null); // { tipo, jti, label }

  const carregar = useCallback(async () => {
    setCarregando(true); setErro("");
    try {
      const r = await authService.listarSessoes();
      setSessoes(r.data || []);
    } catch (e) {
      setErro(e?.message || "Erro ao carregar sessões.");
    }
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const revogar = async (jti) => {
    setErro(""); setMsg("");
    try {
      await authService.revogarSessao(jti);
      setMsg("Sessão encerrada.");
      await carregar();
    } catch (e) { setErro(e?.message || "Erro ao encerrar sessão."); }
  };

  const encerrarOutras = async () => {
    setErro(""); setMsg("");
    try {
      const r = await authService.encerrarOutrasSessoes();
      if (r?.accessToken)  localStorage.setItem("accessToken",  r.accessToken);
      if (r?.refreshToken) localStorage.setItem("refreshToken", r.refreshToken);
      setMsg(r.message || "Outras sessões encerradas.");
      await carregar();
    } catch (e) { setErro(e?.message || "Erro ao encerrar sessões."); }
  };

  const outras = sessoes.filter(s => !s.atual);

  return (
    <div>
      {erro && <Banner tipo="error" onClose={()=>setErro("")}>{erro}</Banner>}
      {msg  && <Banner tipo="success" onClose={()=>setMsg("")}>{msg}</Banner>}

      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
        <div style={{fontSize:13, color:"#6B7280"}}>
          <i className="bi bi-info-circle me-2"/>
          {sessoes.length} sessão(ões) ativa(s) — a marcada com <strong>"atual"</strong> é este dispositivo.
        </div>
        <button onClick={carregar} disabled={carregando}
          style={{background:"none",border:"1px solid #D1D5DB",borderRadius:6,
            padding:"4px 10px", fontSize:12, cursor:"pointer", color:"#6B7280"}}>
          <i className="bi bi-arrow-clockwise me-1"/>Atualizar
        </button>
      </div>

      {outras.length > 0 && (
        <button onClick={() => setConfirma({ tipo:"todas" })}
          style={{
            width:"100%", padding:"10px", marginBottom:14,
            background:"var(--bg-danger-soft)", border:"1px solid #FECACA", color:"#991B1B",
            borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer",
          }}>
          <i className="bi bi-power me-2"/>Encerrar TODAS as outras sessões ({outras.length})
        </button>
      )}

      {carregando ? (
        <div style={{textAlign:"center", padding:30, color:"#6B7280"}}>
          <span className="spinner-border spinner-border-sm me-2"/>Carregando…
        </div>
      ) : sessoes.length === 0 ? (
        <div style={{textAlign:"center", padding:30, color:"#9CA3AF", fontSize:13}}>
          Nenhuma sessão ativa.
        </div>
      ) : (
        <div style={{display:"flex", flexDirection:"column", gap:10}}>
          {sessoes.map(s => (
            <div key={s.jti}
              style={{
                border:"1px solid " + (s.atual ? "#86EFAC" : "#E5E7EB"),
                background:  s.atual ? "#F0FDF4" : "#fff",
                borderRadius:10, padding:"12px 14px",
                display:"flex", alignItems:"center", gap:12,
              }}>
              <div style={{
                width:38, height:38, borderRadius:8, background: s.atual?"#DCFCE7":"#F3F4F6",
                display:"flex",alignItems:"center",justifyContent:"center", flexShrink:0,
              }}>
                <i className="bi bi-laptop"
                  style={{fontSize:18, color: s.atual?"#166534":"#6B7280"}}/>
              </div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:14, fontWeight:600, color:"#111827",
                  display:"flex", alignItems:"center", gap:8}}>
                  {s.deviceLabel || "Dispositivo desconhecido"}
                  {s.atual && (
                    <span style={{background:"#16A34A",color:"#fff",fontSize:10,
                      padding:"2px 8px", borderRadius:10, fontWeight:700}}>
                      ATUAL
                    </span>
                  )}
                </div>
                <div style={{fontSize:12, color:"#6B7280", marginTop:2,
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                  IP: {s.ip || "—"} • Criada: {formatarData(s.dtCriacao)}
                </div>
                <div style={{fontSize:11, color:"#9CA3AF", marginTop:2}}>
                  Último acesso: {tempoRelativo(s.dtUltimoAcesso)}
                </div>
              </div>
              {!s.atual && (
                <button onClick={() => setConfirma({ tipo:"uma", jti:s.jti, label:s.deviceLabel })}
                  title="Encerrar esta sessão"
                  style={{background:"none",border:"1px solid #FECACA",borderRadius:6,
                    color:"#991B1B", padding:"6px 10px", fontSize:12, cursor:"pointer",
                    flexShrink:0}}>
                  <i className="bi bi-power me-1"/>Encerrar
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {confirma && (
        <ConfirmModal
          titulo={confirma.tipo === "todas" ? "Encerrar todas as outras sessões?" : "Encerrar sessão?"}
          mensagem={confirma.tipo === "todas"
            ? `Você será mantido conectado apenas neste dispositivo. Todas as ${outras.length} outras sessões serão deslogadas imediatamente.`
            : `A sessão "${confirma.label || "desconhecida"}" será encerrada imediatamente.`}
          perigo
          textoOk="Encerrar"
          onConfirm={async () => {
            const c = confirma;
            setConfirma(null);
            if (c.tipo === "todas") await encerrarOutras();
            else await revogar(c.jti);
          }}
          onCancel={() => setConfirma(null)}
        />
      )}
    </div>
  );
}

// ── Aba: Notificações (Web Push) ───────────────────────────────────────────
function AbaNotificacoes() {
  const [status, setStatus]   = useState({ suportado: true, assinado: false, permissao: "default" });
  const [server, setServer]   = useState({ checked: false, enabled: false });
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState("");
  const [erro, setErro]       = useState("");

  const refresh = useCallback(async () => {
    const [s, srv] = await Promise.all([
      statusAssinaturaAtual({ admin: true }),
      servidorHabilitado({ admin: true }),
    ]);
    setStatus(s);
    setServer({ checked: true, enabled: srv.enabled });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleAtivar = async () => {
    setErro(""); setMsg(""); setBusy(true);
    try {
      await ativarPush({ admin: true });
      setMsg("Notificações ativadas neste dispositivo.");
      await refresh();
    } catch (e) { setErro(e?.message || "Falha ao ativar notificações."); }
    finally { setBusy(false); }
  };

  const handleDesativar = async () => {
    setErro(""); setMsg(""); setBusy(true);
    try {
      await desativarPush({ admin: true });
      setMsg("Notificações desativadas neste dispositivo.");
      await refresh();
    } catch (e) { setErro(e?.message || "Falha ao desativar."); }
    finally { setBusy(false); }
  };

  const handleTestar = async () => {
    setErro(""); setMsg(""); setBusy(true);
    try {
      const r = await testarPush({ admin: true });
      setMsg(r?.message || "Push enviado.");
    } catch (e) { setErro(e?.message || "Falha ao testar."); }
    finally { setBusy(false); }
  };

  if (!pushSuportado()) {
    return (
      <Banner tipo="warning"
        titulo="Notificações não suportadas"
        descricao="Este navegador não oferece suporte a Web Push. Use Chrome, Edge, Firefox ou Safari recentes."/>
    );
  }

  const permTxt = permissaoAtual() === "granted" ? "Permitida"
                : permissaoAtual() === "denied"  ? "Bloqueada"
                : "Não solicitada";
  const corPerm = permissaoAtual() === "granted" ? "#166534"
                : permissaoAtual() === "denied"  ? "#991B1B" : "#92400E";

  return (
    <div>
      <div style={{fontSize:13, color:"#6B7280", marginBottom:14}}>
        <i className="bi bi-info-circle me-2"/>
        Receba avisos em tempo real <strong>diretamente neste navegador</strong> (mesmo com a aba fechada)
        quando houver: nova reserva solicitada pelo portal, ocorrências urgentes, etc.
      </div>

      <div style={{
        border:"1px solid #E5E7EB", borderRadius:10, padding:14, marginBottom:14,
        background: status.assinado ? "#F0FDF4" : "#FAFAFA",
      }}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6}}>
          <span style={{fontWeight:600, fontSize:13.5, color:"#1F2937"}}>
            <i className={`bi ${status.assinado ? "bi-bell-fill" : "bi-bell-slash"} me-2`}
              style={{color: status.assinado ? "#16A34A" : "#9CA3AF"}}/>
            Status deste dispositivo
          </span>
          <span style={{
            fontSize:11.5, fontWeight:700, padding:"2px 10px", borderRadius:12,
            background: status.assinado ? "#DCFCE7" : "#F3F4F6",
            color: status.assinado ? "#166534" : "#6B7280",
          }}>
            {status.assinado ? "ATIVO" : "INATIVO"}
          </span>
        </div>
        <div style={{fontSize:12.5, color:"var(--gray-600)"}}>
          Permissão do navegador: <strong style={{color:corPerm}}>{permTxt}</strong>
        </div>
      </div>

      {msg  && <Banner tipo="success" descricao={msg}  onFechar={()=>setMsg("")}/>}
      {erro && <Banner tipo="error"   descricao={erro} onFechar={()=>setErro("")}/>}

      {server.checked && !server.enabled && (
        <div style={{
          marginBottom:12, fontSize:12.5, color:"#92400E",
          background:"var(--bg-warning-soft)", padding:"10px 12px", borderRadius:8, border:"1px solid #FDE68A",
        }}>
          <i className="bi bi-shield-exclamation me-2"/>
          <strong>Push desabilitado no servidor.</strong> As chaves VAPID não foram
          configuradas no backend. Mesmo concedendo permissão no navegador, este
          dispositivo não conseguirá se inscrever até o administrador configurar
          <code> VAPID_PUBLIC_KEY</code> / <code>VAPID_PRIVATE_KEY</code> no <code>.env</code> da API.
        </div>
      )}

      <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
        {!status.assinado ? (
          <button onClick={handleAtivar} disabled={busy || (server.checked && !server.enabled)}
            className="btn btn-primary" style={{fontSize:13, padding:"7px 14px"}}>
            <i className="bi bi-bell me-1"/>Ativar neste dispositivo
          </button>
        ) : (
          <>
            <button onClick={handleTestar} disabled={busy}
              className="btn btn-outline-primary" style={{fontSize:13, padding:"7px 14px"}}>
              <i className="bi bi-send me-1"/>Enviar teste
            </button>
            <button onClick={handleDesativar} disabled={busy}
              className="btn btn-outline-danger" style={{fontSize:13, padding:"7px 14px"}}>
              <i className="bi bi-bell-slash me-1"/>Desativar
            </button>
          </>
        )}
      </div>

      {permissaoAtual() === "denied" && (
        <div style={{marginTop:14, fontSize:12, color:"#92400E",
          background:"var(--bg-warning-soft)", padding:"10px 12px", borderRadius:8, border:"1px solid #FDE68A"}}>
          <i className="bi bi-shield-exclamation me-2"/>
          As notificações estão bloqueadas no navegador. Habilite-as nas configurações do site
          (clique no cadeado ao lado da URL → Notificações → Permitir).
        </div>
      )}
    </div>
  );
}
